import {
  Datagrid,
  TextField,
  TopToolbar,
  NumberField,
  FunctionField,
  useTranslate,
  ExportButton,
  EditButton,
  ShowButton,
  useNotify,
  useGetList,
  Filter,
  AutocompleteInput,
  SelectInput,
  DateInput,
  TextInput,
  ReferenceField,
  NumberInput,
  downloadCSV,
} from "react-admin";
import {
  DateField,
  CustomList,
  EmptyList,
  EmptyMessage,
} from "../../shared/components/index.js";
import { ImportButton } from "react-admin-import-csv";
import { COUPON_STATUSES } from "@local/constants/index.js";
import moment from "moment";
import jsonExport from "jsonexport/dist/index.js";

const CouponFilter = (props) => {
  const notify = useNotify();
  const { data: coupons } = useGetList("Coupon");
  const { data: clients, error } = useGetList("Client", {
    merchant_id: localStorage.getItem("id"),
  });

  if (error) {
    return notify(`resources.notifications.errors.${error.message}`, {
      type: "error",
    });
  }
  return (
    <Filter {...props}>
      <AutocompleteInput
        source="id"
        choices={coupons}
        inputText={(record) => record.id}
        optionText="id"
        optionValue="id"
        debounce={10}
        filterToQuery={(searchText) => ({ id: searchText })}
        alwaysOn
        resettable="true"
      />
      <AutocompleteInput
        source="client_id"
        choices={clients}
        inputText={(record) => record.phone}
        optionText="phone"
        optionValue="id"
        debounce={10}
        filterToQuery={(searchText) => ({ phone: searchText })}
        alwaysOn
        resettable="true"
      />
      <SelectInput
        alwaysOn
        resettable
        source="status"
        choices={Object.keys(COUPON_STATUSES).map((status) => ({
          id: status,
          name: `resources.Coupon.source.status.${status}`,
        }))}
      />
      <TextInput
        alwaysOn
        resettable
        source="fuel_type"
        inputProps={{ maxLength: 100 }}
      />
      <TextInput
        source="coupon_code"
        alwaysOn
        resettable
        inputProps={{ maxLength: 100 }}
      />
      <TextInput
        source="coupon_serial"
        alwaysOn
        resettable
        inputProps={{ maxLength: 100 }}
      />
      <NumberInput source="nominal" alwaysOn resettable="true" />
      <DateInput source="begin_date" alwaysOn />
      <DateInput source="end_date" alwaysOn />
    </Filter>
  );
};

const ListActions = (props) => {
  const notify = useNotify();
  const config = {
    logging: true,
    validateRow: async (row) => {
      if (row.serial_visible) {
        return notify("resources.notifications.errors.already_exist", {
          type: "error",
        });
      }
    },
    disableImportOverwrite: true,
    openAskDecide: true,
    handleClose: true,
    handleAskDecideReplace: true,
    handleAskDecideAddAsNew: true,
    handleAskDecideSkip: true,
    handleAskDecideSkipAll: true,
  };

  return (
    <TopToolbar>
      <ImportButton
        className="button-edit"
        {...props}
        {...config}
        parseConfig={{ dynamicTyping: true }}
      />
      <ExportButton sx={{ color: "#291680", fontSize: "0.855rem" }} />
    </TopToolbar>
  );
};

const exporter = async (coupons, fetchRelatedRecords) => {
  const clients = await fetchRelatedRecords(coupons, "client_id", "Client");

  const couponsWithClientInfo = coupons.map((coupon) => ({
    ...coupon,
    begin_date:
      coupon &&
      coupon.begin_date &&
      moment.unix(coupon.begin_date / 1000).format("DD-MM-YYYY"),
    end_date:
      coupon &&
      coupon.end_date &&
      moment.unix(coupon.end_date / 1000).format("DD-MM-YYYY"),
    valid_period:
      coupon &&
      coupon.valid_period &&
      moment.unix(coupon.valid_period / 1000).format("DD-MM-YYYY HH:mm:ss"),
    shared_at:
      coupon &&
      coupon.shared_at &&
      moment.unix(coupon.shared_at / 1000).format("DD-MM-YYYY HH:mm:ss"),
    reserved_until:
      coupon &&
      coupon.reserved_until &&
      moment.unix(coupon.reserved_until / 1000).format("DD-MM-YYYY HH:mm:ss"),
    created_at:
      coupon &&
      coupon.created_at &&
      moment.unix(coupon.created_at / 1000).format("DD-MM-YYYY HH:mm:ss"),
    updated_at:
      coupon &&
      coupon.updated_at &&
      moment.unix(coupon.updated_at / 1000).format("DD-MM-YYYY HH:mm:ss"),
    client_phone: clients[coupon.client_id] && clients[coupon.client_id].phone,
    client_fn:
      clients[coupon.client_id] &&
      `${clients[coupon.client_id].first_name} ${
        clients[coupon.client_id].last_name
      }`,
  }));

  const result = jsonExport(
    couponsWithClientInfo,
    {
      headers: [
        "id",
        "client_id",
        "client_fn",
        "client_phone",
        "trunc_id",
        "coupon_code",
        "coupon_serial",
        "nominal",
        "fuel_type",
        "status",
        "shared_at",
        "valid_period",
        "reserved_until",
        "created_at",
        "updated_at",
      ],
    },
    (err, csv) => {
      downloadCSV(
        csv,
        `exported_coupons_${new Date()
          .toISOString()
          .replace(/[-:T]/g, "")
          .slice(0, 14)}`
      );
    }
  );

  return result;
};

const CouponList = (props) => {
  const t = useTranslate();

  return (
    <CustomList
      exporter={exporter}
      empty={<EmptyList resource="Coupon" isCreateAllowed={true} />}
      {...props}
      actions={<ListActions {...props} hasCreate />}
      filters={<CouponFilter {...props} />}
    >
      <Datagrid
        empty={<EmptyMessage />}
        rowClick={false}
        bulkActionButtons={false}
      >
        <TextField source="coupon_code" />
        <TextField source="fuel_type" />
        <NumberField source="nominal" />
        <ReferenceField source="client_id" reference="Client" link="show">
          <TextField source="phone" />
        </ReferenceField>
        <ReferenceField source="trunc_id" reference="Trunc" link="show">
          <TextField source="id" />
        </ReferenceField>
        <TextField source="coupon_serial" />
        <FunctionField
          source="status"
          render={(record) =>
            t(`resources.Coupon.source.status.${record.status}`)
          }
        />
        <FunctionField
          source="previous_state"
          render={(record) =>
            record.previous_state
              ? t(`resources.Coupon.source.status.${record.previous_state}`)
              : ""
          }
        />
        <DateField source="valid_period" />
        <FunctionField
          source="begin_date"
          render={(record) =>
            record && record.begin_date
              ? moment.unix(record.begin_date / 1000).format("DD-MM-YYYY")
              : ""
          }
          label="resources.Coupon.fields.begin_date"
        />
        <FunctionField
          source="end_date"
          render={(record) =>
            record && record.end_date
              ? moment.unix(record.end_date / 1000).format("DD-MM-YYYY")
              : ""
          }
          label="resources.Coupon.fields.end_date"
        />
        <DateField source="shared_at" />
        <DateField source="reserved_until" />
        <DateField source="created_at" />
        <DateField source="updated_at" />
        <ShowButton />
        <EditButton />
      </Datagrid>
    </CustomList>
  );
};

export default CouponList;
